import { useCallback, useEffect, useState } from 'react';
import { DICT_CATEGORIES, type DictItemDto } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';

/**
 * 自己维护的下拉选项。
 *
 * ★ 现在只有「特殊物品分类」一类,但这一屏按**类目**做:
 *   下一个「这个下拉能不能自己加几项」的需求一定会来,
 *   到时候是加一行注册,不是再抄一屏界面。
 *
 * ★ 每个类目都要说清「加了之后会发生什么」(effect)——
 *   运营看到「特殊物品分类」六个字,不知道加一项会影响谁;
 *   看到「司机会看到高亮提醒,送达时要逐项确认」就知道了。
 */
export function DictSettings({ canManage }: { canManage: boolean }) {
  const [category, setCategory] = useState<string>(DICT_CATEGORIES[0]!.code);
  const [rows, setRows] = useState<DictItemDto[]>([]);
  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const meta = DICT_CATEGORIES.find((c) => c.code === category)!;

  const load = useCallback(async () => {
    try {
      setRows(await api<DictItemDto[]>(`/dict/${category}?includeInactive=true`));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读不到');
    }
  }, [category]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add() {
    if (busy || !label.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/dict/${category}`, {
        method: 'POST',
        // 代号不填 —— 后端会生成一个。让人先想一个英文代号才能加「活鲜」,
        // 这个功能就没人用了
        body: { label: label.trim(), ...(icon.trim() ? { icon: icon.trim() } : {}) },
      });
      setLabel('');
      setIcon('');
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '加不上');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row: DictItemDto) {
    setErr(null);
    try {
      await api(`/dict/items/${row.id}`, {
        method: 'PATCH',
        body: { label: row.label, icon: row.icon, isActive: !row.isActive },
      });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '改不了');
    }
  }

  async function seed() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/dict/${category}/defaults`, { method: 'POST' });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '加不上');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="字典">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">字典</h2>
      <p className="mb-4 text-sm text-slate-500">
        自己维护的下拉选项。这些是**你们自己的业务词**,不该等我们发版才能加
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600 dark:text-slate-300" htmlFor="dict-category">
          类目
        </label>
        <select
          id="dict-category"
          aria-label="字典类目"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm
                     dark:border-slate-600 dark:bg-slate-800"
        >
          {DICT_CATEGORIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* ★ 加了之后会发生什么 —— 不说的话没人知道该不该加 */}
      <p className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500 dark:bg-slate-800/60">
        {meta.effect}
      </p>

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {rows.length === 0 ? (
        <div className="mb-4 rounded-xl border border-dashed border-slate-300 p-6 text-center
                        dark:border-slate-600">
          <p className="mb-3 text-sm text-slate-500">
            一项都还没有 —— 建单时那个下拉会是空的
          </p>
          {canManage && (
            <button
              onClick={() => void seed()}
              disabled={busy}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              放几个常用的进来
            </button>
          )}
        </div>
      ) : (
        <div className="mb-4 max-w-xl overflow-hidden rounded-xl border border-slate-200
                        dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-800">
              <tr>
                <th className="px-3 py-2">名称</th>
                <th className="px-3 py-2">代号</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-3 py-2 text-slate-900 dark:text-slate-100">
                    {r.icon ? `${r.icon} ` : ''}
                    {r.label}
                    {!r.isActive && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500
                                       dark:bg-slate-700">
                        已停用
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{r.code}</td>
                  <td className="px-3 py-2 text-right">
                    {canManage && (
                      <button
                        onClick={() => void toggle(r)}
                        aria-label={`${r.isActive ? '停用' : '启用'} ${r.label}`}
                        className="text-brand-600 hover:underline dark:text-brand-500"
                      >
                        {r.isActive ? '停用' : '启用'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        ★ 停用而不是删除。订单上存的是代号 ——
          真删了的话,历史单上那一项会变成一个查不到含义的字符串,
          而司机端会把它原样显示出来。
      */}
      <p className="mb-4 text-xs text-slate-400">
        停用的分类不会再出现在建单页,但历史订单上还看得到它 ——
        停用不会让车上那箱冻品变成普通货
      </p>

      {canManage && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">加一项</span>
            <input
              aria-label="分类名称"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="例:活鲜"
              className="h-9 w-40 rounded-lg border border-slate-300 bg-white px-3
                         dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">图标(选填)</span>
            <input
              aria-label="分类图标"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="🐟"
              className="h-9 w-20 rounded-lg border border-slate-300 bg-white px-3
                         dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
          <button
            onClick={() => void add()}
            disabled={busy || !label.trim()}
            className="h-9 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            添加
          </button>
        </div>
      )}
    </section>
  );
}
